/**
 * Campaign link capture, e.g. the beta invite email's
 * `tworobots.com/download/?campaign=ALPHA`.
 *
 * Kept apart from the creator code (src/utils/referral.js) on purpose: a player
 * has one creator, ever, and a campaign must never take that place. The claim
 * page (/claim) sends both, and the game backend applies both.
 *
 * Last touch wins (a newer invite replaces an older one), unlike the creator
 * code. The backend decides whether the campaign is still running; this only
 * carries the code from the link to the claim page.
 */

const STORAGE_KEY = 'tr_campaign'
const PARAM_KEY = 'campaign'
// How long a click is remembered. The campaign's own end date still applies.
const WINDOW_DAYS = 30
const DAY_MS = 864e5

/** Codes are 4-16 of A-Z, 0-9, _ on the backend; anything else is dropped. */
function clean(raw) {
  if (!raw) return ''
  return String(raw)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_]/g, '')
    .slice(0, 16)
}

function storage() {
  try {
    return window.localStorage
  } catch (_) {
    return null // blocked storage: the player can still type the code
  }
}

/** The stored campaign code, or '' (absent or expired). */
export function getCampaignCode() {
  if (typeof window === 'undefined') return ''
  const store = storage()
  try {
    const parsed = JSON.parse((store && store.getItem(STORAGE_KEY)) || 'null')
    if (!parsed || !parsed.code) return ''
    if (parsed.ts && Date.now() - parsed.ts > WINDOW_DAYS * DAY_MS) {
      store.removeItem(STORAGE_KEY)
      return ''
    }
    return clean(parsed.code)
  } catch (_) {
    return '' // malformed storage
  }
}

/** Store a campaign code, replacing any earlier one. Returns the stored code. */
export function setCampaignCode(code) {
  if (typeof window === 'undefined') return ''
  const cleaned = clean(code)
  if (!cleaned) return ''
  try {
    storage()?.setItem(
      STORAGE_KEY,
      JSON.stringify({ code: cleaned, ts: Date.now() })
    )
  } catch (_) {
    /* storage full or blocked */
  }
  return cleaned
}

/** Forget the campaign once the claim page has an answer for it. */
export function forgetCampaignCode() {
  if (typeof window === 'undefined') return
  try {
    storage()?.removeItem(STORAGE_KEY)
  } catch (_) {
    /* nothing stored */
  }
}

/**
 * Read `?campaign=` from the current URL and store it. Safe to call on every
 * route change. Returns the code it stored, or ''.
 *
 * @param {string} [search] optional location.search; defaults to window's.
 */
export function captureCampaignFromLocation(search) {
  if (typeof window === 'undefined') return ''
  try {
    const params = new URLSearchParams(
      search != null ? search : window.location.search
    )
    return setCampaignCode(params.get(PARAM_KEY))
  } catch (_) {
    return ''
  }
}
